// ============================================================
// ANSICHTSSACHE-AGENT – Eine Karte auf einem Blatt, die rollt
// ============================================================
// Die Gestalt der Belegkarten am Kundenblatt (`DocumentCard` in
// `kunden/[id]/company-documents.tsx`) ohne deren Zeitachse: Titel
// mit Anzahl und rechts ein Knopf, darunter ALLE Zeilen in einer
// Rollflaeche fester Hoehe, unten die Summenzeile mit zwei Zeilen.
// Entstanden mit dem Website- und dem Serverblatt (08.10.2026);
// die Belegkarten am Kunden sind dieselbe Figur und sollen bei
// naechster Gelegenheit auf dieses Bauteil ziehen.
//
// Warum fest und rollend: eine Karte, deren Hoehe am Bestand
// haengt, macht jedes Blatt anders lang und schiebt die Nachbarn
// derselben Rasterzeile unter den Falz. Der Bestand bestimmt die
// Hoehe deshalb NIE: die Rollflaeche liegt absolut in der Karte und
// traegt nichts zu ihrer Hoehe bei. Die Karte ist mindestens 22rem
// hoch — und so hoch wie die hoechste Nachbarin ihrer Rasterzeile;
// die Rollflaeche fuellt sie dann ganz aus (Ansage vom 09.10.2026:
// „Card immer auf volle Höhe expanden“ — vorher stand unter einer
// festen Rollflaeche Weiss, sobald die Nachbarin hoeher war).
//
// Die Summenzeile hat IMMER zwei Zeilen (oben Wort und Betrag,
// darunter die Nebenrechnung, gekuerzt statt umbrochen) — sonst
// beginnt der graue Fuss der einen Karte hoeher als der daneben.
// ============================================================

import Link from "next/link";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { LISTENFUSS } from "./rahmen";
import { UEBER_DER_ZEILE, Zeilenverweis } from "./zeilenverweis";

export function Blattkarte({
  titel,
  anzahl,
  aktion,
  leer,
  fuss,
  hoehe = "min-h-[22rem]",
  className,
  children,
}: {
  titel: string;
  /** Steht in Klammern hinter dem Titel; 0 zeigt `leer`. */
  anzahl?: number;
  /** Rechts in der Titelzeile, hoechstens 28 px hoch. */
  aktion?: React.ReactNode;
  /** Was dasteht, solange die Karte nichts hat. */
  leer?: React.ReactNode;
  fuss?: {
    /** Links oben, das Wort. */
    label: React.ReactNode;
    /** Rechts oben, der eine Betrag. */
    wert?: React.ReactNode;
    /** Die Nebenrechnung in der zweiten Zeile. */
    notiz?: string;
  };
  /**
   * Mindesthoehe der Rollflaeche als ganze Klasse, damit Tailwind sie
   * findet; hoeher wird sie mit der Karte. `null` fuer eine Karte aus
   * Feldern statt aus Bestand: sie waechst mit ihrem Inhalt, die
   * Summenzeile steht trotzdem unten.
   */
  hoehe?: string | null;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <Card className={cn("flex flex-col overflow-hidden", className)}>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <CardTitle>
            {titel}
            {anzahl !== undefined && ` (${anzahl})`}
          </CardTitle>
          {/* Ein 28 px hoher Knopf an der 22 px hohen Titelzeile:
              der negative Aussenabstand haengt ihn mittig daran,
              ohne die Zeile aufzuspreizen. */}
          {aktion && <div className="-my-[3px] ml-auto flex items-center gap-2">{aktion}</div>}
        </div>
      </CardHeader>
      <CardContent className={cn("flex-1 p-0", hoehe && cn("relative", hoehe))}>
        <div className={cn(hoehe && "absolute inset-0 overflow-y-auto", "px-5 py-2")}>
          {anzahl === 0 && leer ? (
            <div className="text-body-sm text-text-tertiary py-2">{leer}</div>
          ) : (
            children
          )}
        </div>
      </CardContent>
      {fuss && (
        <div className={LISTENFUSS}>
          <div className="flex items-center justify-between gap-3">
            <span className="text-body-sm text-text-primary min-w-0 truncate font-medium">
              {fuss.label}
            </span>
            {fuss.wert !== undefined && (
              <span className="numeric text-body-sm text-text-primary shrink-0 font-medium">
                {fuss.wert}
              </span>
            )}
          </div>
          {/* Auch leer eine Zeile hoch — mit GESCHUETZTEM Leerzeichen,
              ein gewoehnliches faellt im Absatz weg. */}
          <p className="text-caption text-text-tertiary truncate" title={fuss.notiz}>
            {fuss.notiz ?? " "}
          </p>
        </div>
      )}
    </Card>
  );
}

/**
 * Eine Zeile in der Karte: links Titel und Unterzeile, rechts Betrag
 * und Marke. Mit `href` ist die ganze Zeile der Verweis.
 */
export function Blattzeile({
  href,
  extern = false,
  titel,
  unter,
  rechts,
  betrag,
  aktion,
  ton = "text-text-primary",
  marke,
}: {
  href?: string;
  /** Verweis in einen neuen Reiter (Website, Beleg beim Anbieter). */
  extern?: boolean;
  titel: React.ReactNode;
  unter?: React.ReactNode;
  /** Steht vor dem Betrag — eine Marke, ein Zustand. */
  rechts?: React.ReactNode;
  betrag?: React.ReactNode;
  /**
   * Ein Knopf ganz rechts, hinter dem Betrag (Loesen, Entfernen). Mit
   * `href` liegt er ueber der Zeilenflaeche — ein Knopf in einem
   * Verweis waere keiner.
   */
  aktion?: React.ReactNode;
  /** Farbe des Betrags (gedaempft fuer Leeres, Warnung fuer Fehlendes). */
  ton?: string;
  /**
   * Ein Punkt in der Steuerspalte links vor dem Titel: gruen fuer
   * Erledigtes, grau fuer Offenes (Rechnung zugeordnet, Buchung ohne
   * Rechnung). `label` sagt es dem Vorleser und beim Ueberfahren.
   */
  marke?: { ton: "erfolg" | "leise" | "warnung"; label: string };
}) {
  const zeile =
    "border-border -mx-2 flex items-center justify-between gap-3 rounded border-b px-2 py-2 last:border-0";
  const flaeche = "hover:bg-bg-secondary duration-fast transition-colors";
  const titelzeile = <span className="text-body-sm block truncate font-medium">{titel}</span>;
  // Aussen vor der Zeilenflaeche, mittig auf der Titelzeile — die
  // Textkante bleibt, wo sie ist.
  const punkt = marke && (
    <span
      role="img"
      data-steuerspalte
      aria-label={marke.label}
      title={marke.label}
      className={cn(
        "absolute top-[15px] -left-2 h-2 w-2 rounded-full",
        marke.ton === "erfolg"
          ? "bg-success"
          : marke.ton === "warnung"
            ? "bg-warning"
            : "bg-border-strong",
      )}
    />
  );
  const inhalt = (mitVerweis: boolean) => (
    <>
      {punkt}
      <span className="min-w-0">
        {mitVerweis && href ? (
          <Zeilenverweis href={href} className="block truncate">
            {titelzeile}
          </Zeilenverweis>
        ) : (
          titelzeile
        )}
        {unter && <span className="text-caption text-text-tertiary block truncate">{unter}</span>}
      </span>
      {(rechts || betrag !== undefined || aktion) && (
        <span className="flex shrink-0 items-center gap-3">
          {rechts}
          {betrag !== undefined && (
            <span className={cn("numeric text-body-sm whitespace-nowrap", ton)}>{betrag}</span>
          )}
          {aktion && <span className={cn(UEBER_DER_ZEILE, "-my-1 -mr-1")}>{aktion}</span>}
        </span>
      )}
    </>
  );
  // Mit Knopf: die Zeile ist ein Block, der Titel traegt den Verweis
  // ueber die ganze Flaeche, der Knopf liegt darueber.
  const lage = marke && "relative";
  if (aktion) return <div className={cn(zeile, "relative", href && flaeche)}>{inhalt(true)}</div>;
  if (!href) return <div className={cn(zeile, lage)}>{inhalt(false)}</div>;
  return extern ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cn(zeile, flaeche, lage)}>
      {inhalt(false)}
    </a>
  ) : (
    <Link href={href} className={cn(zeile, flaeche, lage)}>
      {inhalt(false)}
    </Link>
  );
}
